import { useCallback, useEffect } from 'react';
import create from 'zustand';
import shallow from 'zustand/shallow';
import { useCurrentSite } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import { useAccountExperiment } from '@hotjar/webapp/hooks/useExperiment';
import type { GettingStartedGuideState } from '@hotjar/webapp/features/Activation/GetStartedGuide/types';
import { useGettingStartedState } from '@hotjar/webapp/features/Activation/GetStartedGuide/hooks/useGettingStartedState';
import {
  selectStoreById,
  useGSGStore,
} from '@hotjar/webapp/domains/activation/getting-started-guide';
import { useLocalStorageJSONValue, safeJSONParse } from '@hotjar/insights-common/hooks/useStorage';

type GettingStartedSideBarState = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

export const gettingStartedSideBarState = create<GettingStartedSideBarState>((set) => {
  const isOpenFromLocalStorageValue = safeJSONParse(localStorage.getItem('gsg_sidebar_open'));
  const initialIsOpenValue =
    isOpenFromLocalStorageValue === null
      ? true
      : safeJSONParse(localStorage.getItem('gsg_sidebar_open'));

  return {
    isOpen: !!initialIsOpenValue,
    setIsOpen: (isOpen: boolean) => set({ isOpen }),
  };
});

type UseGettingStartedSideBar = {
  isSideBarEnabled: boolean;
  remainingTasks: number;
  toggleSideBar: () => void;
  isOpen: boolean;
};

export const useGettingStartedSideBar = (): UseGettingStartedSideBar => {
  const { data: currentSite } = useCurrentSite();
  const { data: currentUser } = useCurrentUser();
  const { isOpen, setIsOpen } = gettingStartedSideBarState();
  const [, setIsOpenLocalStorage] = useLocalStorageJSONValue<boolean>('gsg_sidebar_open');
  const { hasLoaded: isGSGLoaded, enabled: isGSGEnabled } = useGSGStore(
    // We use currentSite id so we don't cause re-renders if a new ref
    // to currentSite is provided with the same id
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useCallback((state) => selectStoreById(state, currentSite), [currentSite?.id]),
    shallow,
  ) as GettingStartedGuideState;
  const { remaining: remainingTasks } = useGettingStartedState(currentSite, currentUser);

  const { inVariant1 } = useAccountExperiment(
    'av_sidebar_start_guide',
    currentSite?.account,
    !!currentSite?.account && isGSGLoaded && isGSGEnabled && !!remainingTasks,
  );

  const toggleSideBar = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  useEffect(() => {
    setIsOpenLocalStorage(isOpen);
  }, [isOpen, setIsOpenLocalStorage]);

  return {
    isSideBarEnabled:
      inVariant1 && !!currentSite?.account && isGSGLoaded && isGSGEnabled && !!remainingTasks,
    remainingTasks,
    toggleSideBar,
    isOpen,
  };
};
